/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { redirectToWhatsApp } from "../utils/functions/redirectToWhatsApp";

// Official WhatsApp glyph (filled) — lucide-react has no brand icon, so it's
// inlined here. Renders in `currentColor` so it inherits the FAB's navy text.
const WhatsAppIcon = ({ size = 24, className }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.892c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.454h.006c6.585 0 11.946-5.359 11.949-11.893a11.821 11.821 0 00-3.481-8.463z" />
  </svg>
);

// Phrases that slide out of the (static) WhatsApp logo, one at a time.
const PHRASES = ["Get Support", "Chat on WhatsApp", "Talk to Expert"];

// This app switches to its mobile (bottom-nav) layout at ≤1020px. On mobile the
// FAB appears on the landing page only; on desktop/web it shows on every page.
// It's fixed in the bottom-right corner everywhere — never draggable.
const MOBILE_QUERY = "(max-width: 1020px)";

// eslint-disable-next-line react/prop-types
const GetSupportButton = ({ pathname = "/" }) => {
  const [hover, setHover] = useState(false);
  const [index, setIndex] = useState(0);

  // Viewport class drives landing-page-only visibility on mobile.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(MOBILE_QUERY).matches
      : false,
  );

  // Track the viewport class so mobile visibility reacts to resizes and
  // device-rotation live.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // The label only slides out while the pointer is on the FAB — no auto-loop.
  // While hovered, rotate through the phrases so each one gets a turn.
  useEffect(() => {
    if (!hover) return undefined;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % PHRASES.length),
      1800,
    );
    return () => clearInterval(id);
  }, [hover]);

  // Expanded only on hover; collapsed (icon-only) otherwise.
  const shown = hover;

  // On mobile the FAB is reserved for the landing page only; everywhere else
  // on mobile it stays hidden. Desktop shows it on every page.
  const isLanding = pathname === "/" || pathname === "";
  const visible = !isMobile || isLanding;

  // `onTap` (framer) still fires on a plain click/tap — used instead of onClick
  // to keep the tap animation and gesture handling consistent.
  const handleTap = () =>
    redirectToWhatsApp({
      phone: "918885166002",
      message: "Hi JaaGa Team, I need some support.",
    });

  return (
    <>
      {/* Fixed full-screen, click-through layer that anchors the FAB. */}
      <div className="jh-support-fab-layer">
        {visible && (
          <motion.button
            type="button"
            aria-label="Get Support"
            className="jh-support-fab"
            style={{
              paddingLeft: shown ? 22 : 14,
              gap: shown ? 10 : 0,
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onTap={handleTap}
            whileTap={{ scale: 0.95 }}
          >
            {/* Animated wording, on the left */}
            <span
              className="jh-support-fab-text"
              style={{
                maxWidth: shown ? 180 : 0,
                opacity: shown ? 1 : 0,
                transform: shown ? "translateX(0)" : "translateX(8px)",
              }}
            >
              {PHRASES[index]}
            </span>
            {/* Static WhatsApp logo, on the right */}
            <WhatsAppIcon size={24} className="jh-support-fab-icon" />
          </motion.button>
        )}
      </div>

      <style>{`
        /* Fixed full-viewport boundary for dragging. It's click-through
           (pointer-events:none) so it never blocks the page; only the FAB
           inside it is interactive. Sits above Leaflet map layers (~z 1000)
           but below the app's modals/drawers (z-[1150]+) so dialogs cover it. */
        .jh-support-fab-layer {
          position: fixed;
          inset: 0;
          z-index: 1100;
          pointer-events: none;
        }
        .jh-support-fab {
          position: absolute;
          bottom: 24px;
          right: 20px;
          pointer-events: auto;
          display: inline-flex;
          align-items: center;
          height: 52px;
          padding-right: 14px;
          border: none;
          border-radius: 999px;
          background: linear-gradient(135deg, #ffc629 0%, #f5a300 100%);
          color: #14213d;
          cursor: pointer;
          box-shadow: 0 10px 26px -8px rgba(245, 163, 0, 0.6),
            0 2px 6px rgba(0, 0, 0, 0.15);
          /* NB: no transform here — framer drives the whileTap scale; a CSS
             transform would fight it. */
          transition:
            padding 0.45s cubic-bezier(0.22, 1, 0.36, 1),
            gap 0.45s cubic-bezier(0.22, 1, 0.36, 1),
            box-shadow 0.2s ease;
        }
        .jh-support-fab:hover {
          box-shadow: 0 14px 32px -8px rgba(245, 163, 0, 0.7),
            0 2px 6px rgba(0, 0, 0, 0.18);
        }
        .jh-support-fab-icon { flex-shrink: 0; }
        .jh-support-fab-text {
          overflow: hidden;
          white-space: nowrap;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.01em;
          transition:
            max-width 0.45s cubic-bezier(0.22, 1, 0.36, 1),
            opacity 0.4s ease,
            transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
        }
        /* Below 1020px the fixed mobile bottom tab bar (BottomNav, ~64px
           tall + iOS safe-area) is visible, so lift the FAB clear of it
           instead of letting them overlap. Desktop (>1020px) is unchanged. */
        @media (max-width: 1020px) {
          .jh-support-fab {
            bottom: calc(80px + env(safe-area-inset-bottom, 0px));
            right: 16px;
          }
        }
        @media (max-width: 640px) {
          /* Compact icon-only FAB on phones so the auto-expanding label
             doesn't sit over page content (pagination, bottom CTAs) at the
             bottom-right corner. Tapping still opens WhatsApp support. */
          .jh-support-fab {
            height: 48px;
            padding-left: 14px !important;
            padding-right: 14px;
            gap: 0 !important;
          }
          .jh-support-fab-text {
            max-width: 0 !important;
            opacity: 0 !important;
            transform: none !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .jh-support-fab,
          .jh-support-fab-text { transition: none; }
        }
      `}</style>
    </>
  );
};

export default GetSupportButton;
